import { MD5 } from "crypto-js";
import { IWordWithConfidence, DocumentWord, PostUploadCommands } from "./Azure";
import { logDebug } from "./logger";
import { IDocumentForUI, IEstimaticsData, IPageForUI, IQueuedItemToProcess } from "./EstimateEngine/EstimateEngineTypes";
import { EQuantityTypesLow, EstimaticsFileTypes } from "./interfaces";

export interface IUploadedFileProperties {
	postUploadResults: any[];
	source: string;
	destination: string;
	uploadId: string;
	mime: string;
}
interface IUniqueRoom {
	roomname: string;
	uploadIds: string[];
	sources: string[];
	destinations: string[];
	pageId: string;
}
export default class EngineUtils {
	static verifyQuantity(input: string): { number: number; text: string; modifier?: EQuantityTypesLow } {
		const numberMatch = input.match(/[-+]?(?:\d*\.\d+|\d+)/);
		if (!numberMatch) {
			// test against the available measurements
			if (EngineUtils.availableMeasurementsForAutoModifiersAndAutoCalculation.includes(input.trim().toLowerCase())) {
				return { number: 1, text: input.trim().toLowerCase() };
			}
			throw new Error("No number found in the string.");
		}
		const numberValue = parseFloat(numberMatch[0]);
		if (numberValue <= 0) {
			// const testForModifier = input.replace(numberMatch[0], "-{n}").trim().toLowerCase();
			// if (EngineUtils.availableMeasurementsForAutoModifiersWithSubstraction.includes(testForModifier)) {
			// 	const stringWithoutNumber = input.replace(numberMatch[0], "").trim();
			// 	return {
			// 		number: numberValue,
			// 		text: stringWithoutNumber.trim().toLowerCase(),
			// 		modifier: EQuantityTypesLow.QauntityAsSubtract,
			// 	};
			// }
			throw new Error("Invalid number");
		}
		const stringWithoutNumber = input.replace(numberMatch[0], "").trim();
		const stringParts = stringWithoutNumber.split(/\s+/);

		const measurement = stringParts[0].trim().toLowerCase();
		if (!measurement) {
			return { number: numberValue, text: "" };
		}

		if (stringParts.length > 1) {
			throw new Error("Invalid format");
		}
		if (EngineUtils.availableMeasurementsLow.includes(measurement)) {
			return { number: numberValue, text: measurement };
		} else if (EngineUtils.availableMeasurementsForAutoModifiersAndAutoCalculation.includes(stringParts[0].trim().toLowerCase())) {
			return { number: numberValue, text: stringParts[0].trim(), modifier: EQuantityTypesLow.QuantityAsMultiplier };
		} else {
			throw new Error("Invalid measurement");
		}
	}

	public static getUniqueRooms(data: IUploadedFileProperties[]): IUniqueRoom[] {
		let roomNamesFromImages = data.map((file) => {
			if (file.postUploadResults[0].command === PostUploadCommands.extractRoomnameFromImage) {
				return {
					roomname: file.postUploadResults[0].results.predictions[0].predicted_class,
					uploadId: file.uploadId,
					source: file.source,
					destination: file.destination,
					// TODO :: using the uploadId and source here is buggy, if more photos will be uploaded the uploadId might be different
					// cause we are using the uploadId of the first photo in the room. For now, this is fine, but we need to fix this in the future
					// pageId: EngineUtils.createPageKey(file.uploadId, 'photo', file.source)
				};
			}
		});

		const groupedRooms = roomNamesFromImages
			.reduce((acc, room) => {
				if (room) {
					const existingRoom = acc.find((r) => r.roomname === room.roomname);
					if (existingRoom) {
						existingRoom.uploadIds.push(room.uploadId);
						existingRoom.sources.push(room.source);
						existingRoom.destinations.push(room.destination);
					} else {
						acc.push({
							pageId: "",
							roomname: room.roomname,
							uploadIds: [room.uploadId],
							sources: [room.source],
							destinations: [room.destination],
						});
					}
				}
				return acc;
			}, [] as IUniqueRoom[])
			.map((room) => {
				return {
					...room,
					pageId: EngineUtils.createPageKey(room.uploadIds.join("-"), "photo", room.sources.join("-")),
				};
			});
		logDebug("getUniqueRooms", groupedRooms);
		return groupedRooms;
	}

	public static readonly availableMeasurementsForAutoModifiersAndAutoCalculation = [
		"f",
		"c",
		"w",
		"pc",
		"pf",
		"floor",
		"ceiling",
		"walls",
		"wall",
		"floors",
	];
	public static readonly availableMeasurementsForAutoModifiers = [
		...EngineUtils.availableMeasurementsForAutoModifiersAndAutoCalculation,
		"wc",
		"ll",
		"sl",
		"v",
		"fbs",
		"wbs",
	];
	public static readonly availableMeasurementsForAutoModifiersWithSubstraction =
		EngineUtils.availableMeasurementsForAutoModifiersAndAutoCalculation.map((m) => `${m}-{n}`);
	public static readonly availableMeasurementsBasic = [
		"Floor(F)",
		"Ceiling(C)",
		"Walls(W)",
		"Wall and ceiling(WC)",
		"Perimeter of ceiling(PC)",
		"Perimeter of floor(PF)",
	];
	public static readonly availableMeasurements = [
		...EngineUtils.availableMeasurementsBasic,
		"Long wall(LL)",
		"Short wall(SL)",
		"Volume of room(V)",
		"Floor below stairs(FBS)",
		"Walls below stairs(WBS)",
		"SF",
		"LF",
		"RL",
		"EA",
		"HR",
		"flr_rpl",
		"SQ",
		"DA",
		"WK",
		"GL",
		"CF",
		"MO",
	];

	public static readonly availableMeasurementsLow = EngineUtils.availableMeasurements.map((m) => m.toLowerCase());
	public static readonly availableMeasurementsSpecialLow = EngineUtils.availableMeasurementsLow.filter((m) => m.includes("("));
	// this is a map of common ocr errors found in tic sheets
	// TODO :: need to add more characters to this map as we find more errors

	private static _ocrCorrectionMap: { [s: string]: string } = {
		Á: "A",
		À: "A",
		Â: "A",
		Ä: "A",
		Å: "A",
		Ã: "A",
		á: "a",
		à: "a",
		â: "a",
		ä: "a",
		å: "a",
		ã: "a",
		É: "E",
		È: "E",
		Ê: "E",
		Ë: "E",
		é: "e",
		è: "e",
		ê: "e",
		ë: "e",
		Í: "I",
		Ì: "I",
		Î: "I",
		Ï: "I",
		İ: "I",
		Į: "I",
		Ī: "I",
		Ĭ: "I",
		Ɨ: "I",
		І: "I",
		Ї: "I",
		Ị: "I",
		Ỉ: "I",
		Ɩ: "I",
		Ⅰ: "I",
		ⅈ: "i",
		į: "i",
		í: "i",
		ì: "i",
		î: "i",
		ï: "i",
		"¡": "i",
		Ó: "O",
		Ò: "O",
		Ô: "O",
		Ö: "O",
		Õ: "O",
		ó: "o",
		ò: "o",
		ô: "o",
		ö: "o",
		õ: "o",
		Ú: "U",
		Ù: "U",
		Û: "U",
		Ü: "U",
		ú: "u",
		ù: "u",
		û: "u",
		ü: "u",
		Ç: "C",
		ç: "c",
		Ñ: "N",
		ñ: "n",
		Ý: "Y",
		ý: "y",
		ÿ: "y",
		Ø: "O",
		ø: "o",
		Æ: "AE",
		æ: "ae",
		ß: "ss",
		Œ: "OE",
		œ: "oe",
		"×": "x",
		"\u0307": "", // this is a dot above a character
	};

	public static readonly engineStringTransformers = [
		EngineUtils.replaceBadScanCharactersSafe,
		(s: string) => s.toLowerCase(),
		(s: string) => {
			const tailTrimChars = "_¡.,·¿‘`";
			s = EngineUtils.frontTrimAllTheseChars(s, `${tailTrimChars}-'";:|`);
			s = EngineUtils.endTrimAllTheseChars(s, tailTrimChars);
			return s;
		},
	];

	public static replaceBadScanCharacters(input?: string): string | undefined {
		if (!input) {
			return input;
		}
		return input
			.split("")
			.map((char) => EngineUtils._ocrCorrectionMap[char] || char)
			.join("");
	}
	public static replaceBadScanCharactersSafe(input?: string): string {
		return EngineUtils.replaceBadScanCharacters(input) || "";
	}

	// Returns characters from the longer string that are not in the shorter string.
	public static charactersInLongerThatAreNotInShorter(s0: string, s1: string): string {
		const [longer, shorter] = s0.length >= s1.length ? [s0, s1] : [s1, s0];
		return longer
			.split("")
			.filter((char) => !shorter.includes(char))
			.join("");
	}

	public static countCharHits(s0: string, s1: string): number {
		let hits = 0;
		for (let i = 0; i < s0.length; i++) {
			if (s0[i] === s1[i]) {
				hits++;
			}
		}
		return hits;
	}

	static endTrimAllTheseChars(s: string, arg1: string): string {
		let i = s.length - 1;
		while (i >= 0 && arg1.includes(s[i])) {
			i--;
		}
		return s.slice(0, i + 1);
	}

	static frontTrimAllTheseChars(s: string, arg1: string): string {
		let i = 0;
		while (i < s.length && arg1.includes(s[i])) {
			i++;
		}
		return s.slice(i);
	}

	public static removeAllTheseChars(input: string, chars: string): string {
		return input
			.split("")
			.filter((char) => !chars.includes(char))
			.join("");
	}

	public static applyTransformers(input: string, transformers: ((s: string) => string)[]): string {
		return transformers.reduce((acc, transformer) => transformer(acc), input);
	}

	public static tokenizeWords(
		input: DocumentWord[],
		delimiters: string[],
		transofrmers: ((s: string) => string)[],
	): IWordWithConfidence[] {
		const tokens: IWordWithConfidence[] = [];
		for (const word of input) {
			const wordTokens = EngineUtils.tokenizeString(word.content!, delimiters, transofrmers);
			for (const token of wordTokens) {
				tokens.push({ word: token, confidence: word.confidence });
			}
		}
		return tokens;
	}
	public static tokenizeString(input: string, delimiters: string[], transofrmers: ((s: string) => string)[]): string[] {
		const tokens: string[] = [];
		let currentToken = "";
		for (let i = 0; i < input.length; i++) {
			const char = input[i];
			if (delimiters.includes(char)) {
				if (currentToken) {
					tokens.push(EngineUtils.applyTransformers(currentToken, transofrmers));
					currentToken = "";
				}
				continue;
			}
			currentToken += char;
		}
		if (currentToken) {
			tokens.push(EngineUtils.applyTransformers(currentToken, transofrmers));
		}
		return tokens;
	}

	private static _ocrConfusionMap: string[][] = [
		["l", "1", "i", "I", "|", "!", "j"],
		["o", "0", "O", "Q", "D", "a"],
		["B", "8"],
		["S", "5", "s"],
		["Z", "2", "z", "7"],
		["g", "9", "q"],
		["c", "e"],
		["v", "u", "U", "V"],
		// ['w', 'vv', 'W', 'VV'], // need better getWordReplaceConfusionChars function to handle double chars
		["G", "6"],
	];

	public static getWordReplaceConfusionChars(word: string): string {
		const chars = word.split("");
		const output: string[] = [];
		chars.forEach((char) => {
			let found = false;
			for (const confusionChars of this._ocrConfusionMap) {
				if (confusionChars.includes(char)) {
					output.push(confusionChars[0]);
					found = true;
					break;
				}
			}
			if (!found) {
				output.push(char);
			}
		});
		return output.join("");
	}

	public static pageDataFromPageInfo(pageInfo: { document: IDocumentForUI; page: IPageForUI }): {
		roomName: string;
		pageName: string;
		uploadId: string;
		sourceImageName: string;
		pageId: string;
		itemForDebugOnly: IQueuedItemToProcess | null;
		page: IPageForUI;
	} {
		// TODO :: remove the itemForDebugOnly from this logic.
		const itemForDebugOnly = pageInfo.document.itemForDebugOnly;
		const roomName = pageInfo.document.roomName;
		const pageName = pageInfo.document.originalName;
		const uploadId = pageInfo.document.metadata?.uploadId || itemForDebugOnly?.originalUploadedFiles?.[0]?.uploadId || "";
		const sourceImageName =
			pageInfo.document.metadata?.sourceImageName || itemForDebugOnly?.originalUploadedFiles?.[0]?.originalFileName || "";

		let pageId = pageInfo.document.metadata?.pageId || EngineUtils.createPageKey(uploadId, pageName, sourceImageName);
		return { roomName, pageName, uploadId, sourceImageName, pageId, itemForDebugOnly, page: pageInfo.page };
	}
	public static createPageKey(uploadId: string, pageName: string, sourceImageName: string): string {
		// logDebug("createPageKey", { uploadId, pageName, sourceImageName });
		return MD5(`${uploadId}-${pageName}-${sourceImageName}`).toString();
	}

	public static associateImagesWithRooms(estimaticsData: IEstimaticsData, uploadedFilesProperties?: IUploadedFileProperties[]) {
		const ticSheets = (estimaticsData.files ?? [])
			.filter((file) => file.fileType === EstimaticsFileTypes.tic)
			.map((file) => {
				const prop = uploadedFilesProperties?.find((prop) => prop.uploadId === file.uploadId);
				if (prop) {
					return {
						file,
						prop,
					};
				}
			})
			.filter((x) => x);
		const images = (estimaticsData.files ?? [])
			.filter((file) => file.fileType === EstimaticsFileTypes.image)
			.map((file) => {
				const prop = uploadedFilesProperties?.find((prop) => prop.uploadId === file.uploadId);
				if (prop) {
					return {
						file,
						prop,
					};
				}
			})
			.filter((x) => x);
		const rooms = ticSheets
			.map((tic) => tic?.prop.postUploadResults?.[0]?.results?.rooms?.map((room: any) => ({ room, ...tic })))
			.flat();
		if (rooms.length && images.length > 0) {
			images.forEach((image) => {
				const imageRoomName = image!.prop.postUploadResults?.[0]?.results?.predictions?.[0]?.predicted_class;
				if (imageRoomName) {
					const room = rooms.find((room) => room.room.roomName.toLowerCase() === imageRoomName.toLowerCase());
					if (room) {
						const pageId = EngineUtils.createPageKey(room.file.uploadId, room.room.imageName, room.file.originalname);
						image!.file.assignedToPageId = pageId;
					}
				}
			});
		}
	}
}
